/**
 * Checks 7-9 — the three ways a caption can pass every publisher constraint and
 * still be unusable. Every rule here is a defect measured on real prod rows in
 * the 2026-07-31 workflow audit, not a taste opinion:
 *
 *   7. caption_cta    — 24 of the 40 most recent captions ended in "Save this
 *                       for later"; ZERO carried a purchase CTA, on an account
 *                       whose stated goal was "Sell more beans online".
 *   8. caption_scene  — 13 of 40 narrated a photograph, 10 of them on posts with
 *                       `media_urls = []`: "looking minimal and clean centered
 *                       on the counter", attached to nothing.
 *   9. caption_slop   — the machine-written tells the generator kept reaching
 *                       for. Cheap to detect, expensive to publish.
 *
 * Same hard constraint as every other module here: React-free, DOM-free,
 * Node-free, `@/`-alias-free, relative imports with explicit `.ts`, so Deno can
 * reach it through `supabase/functions/_shared/content-quality.ts`.
 *
 * SEVERITY DISCIPLINE. `caption_cta` and `caption_slop` are capped at WARN
 * forever — they are judgement, and a chip that blocks on judgement cries wolf
 * (see checks/caption.ts:78-85 for the same reasoning about the fold estimate).
 * `caption_scene` is the one that may FAIL, because it is not judgement: a
 * caption that tells the reader to look at a photograph on a post carrying no
 * photograph is factually broken, and reporting that as a pass is exactly the
 * fabricated success this grader exists to end.
 */
import { findSlopPhrases } from '../../content-doctrine/slop.ts';
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

/**
 * Asks that move a reader toward a purchase. Deliberately broader than "buy":
 * a DM-to-order and a link-in-bio both convert; a bookmark does not.
 */
const PURCHASE_CTA_PATTERNS: RegExp[] = [
  /\blink in (?:our )?bio\b/i,
  /\b(?:tap|click|shop|order|buy|grab|get)\s+(?:the\s+)?(?:link|now|yours|one|a bag|it|them|here)\b/i,
  /\bshop (?:the|our|now|it|this)\b/i,
  /\b(?:available|in stock|on sale|now live|out now|restocked)\b.{0,40}\b(?:site|shop|store|online|bio)\b/i,
  /\bDM (?:us|me)\b.{0,30}\b(?:order|buy|purchase|reserve|grab|yours)\b/i,
  // A soft-voice brand's order ask. Caught live: three generated captions
  // closed on "Send us a message if you'd like one set aside" — an order, not
  // an engagement prompt — and the first version of this check warned on all
  // three. A grader that mislabels a real purchase CTA teaches the user to
  // ignore it.
  /\b(?:set aside|reserve(?:d)? (?:one|yours|a bag)|hold (?:one|yours))\b/i,
  /\b(?:add to (?:cart|bag)|checkout|place your order|order yours|treat yourself)\b/i,
  // "add these beans to your cart today" — the literal `add to cart` above
  // misses every form with the product named in between, which is how a real
  // caption actually writes it. Measured: run 5 found the at-scale purchase_cta
  // counter reading 3 of 5 where the eye read 5 of 5, and this was one of the
  // two misses.
  /\badd\b[^.!?]{0,40}\bto (?:your |the )?(?:cart|bag|basket)\b/i,
  /\bswipe up\b/i,
  /\bshop(?:ping)? link\b/i,

  // ─────────────────────────────────────────────────────────────────────────
  // SOFTWARE AND SERVICES. Everything above assumes a physical checkout, so a
  // SaaS or agency account scored "no purchase CTA" on captions whose whole
  // job was conversion — the ask is a trial, a demo or a signup, and none of
  // them contain the word "shop". Of the 6 accounts on this project that have
  // ever generated content, 4 have zero products, so this is the majority path
  // (see `docs/planning/ICP-saas-launch-2026-08-01.md`).
  //
  // "try it" is included deliberately: for a launch it IS the conversion ask,
  // and it is also the only Product-Hunt-compliant phrasing, since asking for
  // votes breaks their rules (`checks/platform-rules.ts`).
  /\b(?:start|begin|claim)\s+(?:your\s+)?(?:free\s+)?(?:trial|plan|account)\b/i,
  /\bfree trial\b/i,
  /\b(?:book|schedule|grab)\s+(?:a\s+)?(?:demo|call|slot|time|intro)\b/i,
  /\b(?:sign|signing)\s*up\b/i,
  /\bcreate (?:your|an|a) (?:free )?account\b/i,
  /\b(?:try|test)\s+it\b/i,
  /\btry (?:us|mora|the app|it out)\b/i,
  /\bget (?:early )?access\b/i,
  /\bjoin the (?:waitlist|beta|early access)\b/i,
  /\b(?:install|download)\s+(?:it|the app|now)\b/i,
];

const SAVE_CTA_PATTERNS: RegExp[] = [
  /\bsave (?:this|it|these|for)\b/i,
  /\bbookmark\b/i,
  /\bpin (?:this|it)\b/i,
];

export function hasPurchaseCta(caption: string): boolean {
  return PURCHASE_CTA_PATTERNS.some((pattern) => pattern.test(caption));
}

export function hasSaveCta(caption: string): boolean {
  return SAVE_CTA_PATTERNS.some((pattern) => pattern.test(caption));
}

/**
 * Only meaningful when we know the campaign is trying to sell. `salesGoal`
 * undefined ⇒ `skipped`, never `pass` — the same discipline the aspect check
 * uses for unknown image dimensions. Claiming "no purchase CTA needed" for a
 * campaign whose goal we never received would be a guess reported as a result.
 */
export function checkCaptionCta(input: { caption: string; salesGoal?: boolean }): QualityCheck {
  const base = { id: 'caption_cta' as const, label: CHECK_LABEL.caption_cta };
  const caption = (input.caption ?? '').trim();

  if (!caption) {
    return { ...base, status: 'skipped', detail: 'No caption to check yet.' };
  }
  if (input.salesGoal !== true) {
    return {
      ...base,
      status: 'skipped',
      detail: 'Mora only checks for a buying CTA when the campaign goal is to sell.',
    };
  }

  const purchase = hasPurchaseCta(caption);
  const save = hasSaveCta(caption);

  if (purchase) {
    return {
      ...base,
      status: 'pass',
      detail: 'This caption asks the reader to buy — which is what the campaign goal asked for.',
    };
  }
  if (save) {
    return {
      ...base,
      status: 'warn',
      detail:
        'The goal of this campaign is to sell, but the caption ends on a save or bookmark. Nobody buys anything by bookmarking it.',
    };
  }
  return {
    ...base,
    status: 'warn',
    detail:
      'The goal of this campaign is to sell, and this caption never asks the reader to buy or where to go.',
  };
}

/**
 * Language that only makes sense if the reader is looking at a picture.
 * Written to catch the observed failures without firing on ordinary prose:
 * "pictured here", "in this shot", "centered on the counter", "that warm light",
 * "as you can see".
 */
const SCENE_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\bpictured (?:here|above|below)\b/i, label: 'pictured here' },
  { re: /\bin (?:this|the) (?:shot|photo|picture|image|frame)\b/i, label: 'in this shot' },
  { re: /\b(?:shown|seen|captured) (?:here|above|below)\b/i, label: 'shown here' },
  { re: /\bas you can see\b/i, label: 'as you can see' },
  { re: /\bswipe (?:through|to see|for)\b/i, label: 'swipe through' },
  {
    re: /\b(?:centered|centred|nestled|resting|sitting|styled|framed|perched)\s+(?:on|in|against|atop|beside)\b/i,
    label: 'centered on / resting on',
  },
  {
    re: /\b(?:soft|warm|golden|moody|natural|morning|afternoon)\s+(?:light|lighting|sunlight|glow)\s+(?:hits?|falls?|catches|resting|spilling|pouring|streaming|washing)\b/i,
    label: 'soft light hits',
  },
  { re: /\blook(?:ing)?\s+(?:minimal|clean|effortless|styled)\b/i, label: 'looking minimal/clean' },
  { re: /\b(?:this|the) (?:flatlay|close-?up|hero shot|product shot)\b/i, label: 'this flatlay' },
  {
    re: /\bagainst a (?:minimal|clean|neutral|soft|white|dark) (?:backdrop|background)\b/i,
    label: 'against a minimal backdrop',
  },
];

export function findSceneLanguage(caption: string): string[] {
  const found: string[] = [];
  for (const { re, label } of SCENE_PATTERNS) {
    if (re.test(caption) && !found.includes(label)) found.push(label);
  }
  return found;
}

export function checkCaptionScene(input: { caption: string; mediaUrls: string[] }): QualityCheck {
  const base = { id: 'caption_scene' as const, label: CHECK_LABEL.caption_scene };
  const caption = (input.caption ?? '').trim();
  const hasMedia = Array.isArray(input.mediaUrls) && input.mediaUrls.length > 0;

  if (!caption) {
    return { ...base, status: 'skipped', detail: 'No caption to check yet.' };
  }

  const scene = findSceneLanguage(caption);

  if (hasMedia) {
    if (scene.length > 0) {
      return {
        ...base,
        status: 'warn',
        detail: `This caption narrates the image (${scene.join(', ')}). The reader can already see it — that space is better spent on the hook.`,
      };
    }
    return {
      ...base,
      status: 'pass',
      detail: 'The caption works alongside the image instead of describing it.',
    };
  }

  if (scene.length > 0) {
    return {
      ...base,
      status: 'fail',
      detail: `This post has no image, but the caption describes one (${scene.join(', ')}). Readers will see text referring to a picture that is not there.`,
    };
  }

  return {
    ...base,
    status: 'pass',
    detail: 'This is a text-only post and the caption does not reference an image.',
  };
}

/**
 * The 17 labels this check has always reported, now backed by
 * `src/lib/content-doctrine/slop.ts`'s shared `SLOP_PHRASES_STANDARD` tier
 * instead of a hand-kept `{re, label}` array (2026-09-15 slop-vocabulary
 * consolidation — the third of three duplicated lists; see that file's header
 * comment for the other two).
 *
 * `findSlopPhrases(caption, 'standard')` returns every STANDARD-tier label
 * that matched, including some this surface never reported before (CORE's
 * own finer-grained labels like `elevate your`, which STANDARD includes
 * alongside the grouped `elevate` label this check has always used). This
 * allowlist is what keeps `findSlopMarkers`'s OUTPUT identical to before: it
 * reports only the labels this check has always known about, filtering out
 * the CORE-specific ones that exist for `ad-contract.ts`'s benefit, not this
 * one's.
 */
const CAPTION_SLOP_LABELS: readonly string[] = [
  'elevate',
  'unleash',
  'game-changer',
  'dive into',
  "in today's fast-paced world",
  'in the world of',
  'look no further',
  'unlock the',
  'level up your',
  'revolutionize',
  'supercharge',
  'seamless',
  'harness the power',
  'the perfect blend of',
  'say goodbye to',
  "it's not just a",
  'a testament to',
];

export function findSlopMarkers(caption: string): string[] {
  const allowed = new Set(CAPTION_SLOP_LABELS);
  return findSlopPhrases(caption, 'standard')
    .map((match) => match.label)
    .filter((label) => allowed.has(label));
}

export function checkCaptionSlop(input: { caption: string }): QualityCheck {
  const base = { id: 'caption_slop' as const, label: CHECK_LABEL.caption_slop };
  const caption = (input.caption ?? '').trim();

  if (!caption) {
    return { ...base, status: 'skipped', detail: 'No caption to check yet.' };
  }

  const markers = findSlopMarkers(caption);
  if (markers.length > 0) {
    return {
      ...base,
      status: 'warn',
      detail: `${markers.map((m) => `"${m}"`).join(', ')} — phrasing readers recognise as machine-written. Worth a rewrite before this goes out.`,
    };
  }

  return {
    ...base,
    status: 'pass',
    detail: 'No stock AI phrasing in this caption.',
  };
}
